import { useState } from 'react';
import { Plus, Check } from 'lucide-react';
import { api } from '../api';
import { useLoad } from '../hooks/useLoad';
import { PageHeader, Card, Badge, EmptyState, cx } from '../components/ui';
import { DomainsLibrary } from './DomainsLibrary';

// Library (PRD §3.6/§5.5/§2.8): browse the shared global content + domain pool, "Use" to clone a
// copy into your tenant. Thin consumer — all logic is server-side (clone is a /v1 POST).
type Kind = 'templates' | 'landing-pages' | 'training-moments' | 'domains';
const TABS: { key: Kind; label: string }[] = [
  { key: 'templates', label: 'Templates' },
  { key: 'landing-pages', label: 'Landing Pages' },
  { key: 'training-moments', label: 'Training Moments' },
  { key: 'domains', label: 'Domains' },
];
const NOUN: Record<string, string> = {
  templates: 'templates',
  'landing-pages': 'landing pages',
  'training-moments': 'training moments',
};

interface LibItem {
  id: string;
  name: string;
  subject?: string;
  category?: string | null;
}
function ContentTab({ kind }: { kind: Exclude<Kind, 'domains'> }) {
  const { data, error } = useLoad(
    () => api.get<{ data: LibItem[] }>(`/v1/${kind}?library=true`),
    [kind],
  );
  const [cloned, setCloned] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const items = data?.data ?? [];

  async function use(id: string) {
    setBusy(id);
    try {
      await api.post(`/v1/${kind}/${id}/clone`);
      setCloned((c) => ({ ...c, [id]: true }));
    } finally {
      setBusy(null);
    }
  }

  if (error)
    return (
      <div className="rounded-input border border-line bg-ink/[0.03] px-3 py-2 text-sm text-muted">
        {error}
      </div>
    );
  if (items.length === 0)
    return (
      <Card>
        <EmptyState
          icon={Plus}
          title={`No library ${NOUN[kind]} yet`}
          hint="Shared content will appear here as it's published."
        />
      </Card>
    );

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {items.map((it) => (
        <Card key={it.id} className="flex flex-col p-5">
          {it.category && (
            <div>
              <Badge tone="muted">{it.category}</Badge>
            </div>
          )}
          <div className="mt-3 font-display text-lg text-ink">{it.name}</div>
          {it.subject && <div className="mt-1 truncate text-sm text-muted">{it.subject}</div>}
          <div className="mt-4 flex-1" />
          <button
            onClick={() => void use(it.id)}
            disabled={busy === it.id || cloned[it.id]}
            className="btn-outline mt-3 w-full"
          >
            {cloned[it.id] ? (
              <>
                <Check className="h-4 w-4" /> Added to your content
              </>
            ) : (
              <>
                <Plus className="h-4 w-4" /> Use this
              </>
            )}
          </button>
        </Card>
      ))}
    </div>
  );
}

export function LibraryPage() {
  const [tab, setTab] = useState<Kind>('templates');
  return (
    <div>
      <PageHeader
        title="Library"
        subtitle="Shared templates, pages, and sending domains you can use in any campaign."
      />
      <div className="mb-6 flex flex-wrap gap-1 border-b border-line">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={cx(
              '-mb-px border-b-2 px-4 py-2.5 text-sm font-medium transition-colors',
              tab === t.key
                ? 'border-ink text-ink'
                : 'border-transparent text-muted hover:text-ink',
            )}
          >
            {t.label}
          </button>
        ))}
      </div>
      {tab === 'domains' ? <DomainsLibrary /> : <ContentTab kind={tab} />}
    </div>
  );
}
